import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AgoraRTC from "agora-rtc-sdk-ng";

AgoraRTC.setLogLevel(4); // suppress verbose logs

// ── Icons ─────────────────────────────────────────────────────────────
function MicIcon({ muted }) {
  return muted ? (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23"/>
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ) : (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  );
}

function CamIcon({ muted }) {
  return muted ? (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34"/>
      <path d="M23 7l-7 5 7 5V7z"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  );
}

function PhoneOffIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07"/>
      <path d="M14.58 4.07A19.77 19.77 0 0 0 3.07 12.7 2 2 0 0 0 5 14.9l3 .01a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L9 22.81"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// ── Local video element ───────────────────────────────────────────────
function LocalVideo({ track, videoMuted }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!track || !ref.current) return;
    track.play(ref.current);
    return () => { try { track.stop(); } catch (_) {} };
  }, [track]);

  return (
    <div style={{
      position: "absolute", bottom: "100px", right: "20px",
      width: "200px", height: "150px",
      borderRadius: "16px", overflow: "hidden",
      border: "2px solid rgba(255,255,255,0.15)",
      background: "#1a1a2e",
      boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
      zIndex: 10,
    }}>
      <div ref={ref} style={{ width: "100%", height: "100%", display: videoMuted ? "none" : "block" }} />
      {videoMuted && (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#1a1a2e" }}>
          <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
        </div>
      )}
      <p style={{ position: "absolute", bottom: "8px", left: "10px", fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
        You
      </p>
    </div>
  );
}

// ── Remote video element ──────────────────────────────────────────────
function RemoteVideo({ user }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!user?.videoTrack || !ref.current) return;
    user.videoTrack.play(ref.current);
    return () => { try { user.videoTrack.stop(); } catch (_) {} };
  }, [user?.videoTrack]);

  const hasVideo = !!user?.videoTrack;

  return (
    <div style={{ width: "100%", height: "100%", position: "relative", background: "#0f0f1e" }}>
      <div ref={ref} style={{ width: "100%", height: "100%", display: hasVideo ? "block" : "none" }} />
      {!hasVideo && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" }}>
          <div style={{ width: "88px", height: "88px", borderRadius: "50%", background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "rgba(255,255,255,0.3)" }}>
            Camera is off
          </p>
        </div>
      )}
    </div>
  );
}

// ── Waiting screen (before anyone else joins) ─────────────────────────
function WaitingScreen() {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px", background: "#0f0f1e" }}>
      <div style={{ position: "relative", width: "80px", height: "80px" }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid rgba(0,45,181,0.3)", animation: "ping 2s ease-in-out infinite" }} />
        <div style={{ position: "absolute", inset: "10px", borderRadius: "50%", border: "2px solid rgba(0,45,181,0.5)", animation: "ping 2s ease-in-out infinite 0.5s" }} />
        <div style={{ position: "absolute", inset: "20px", borderRadius: "50%", background: "linear-gradient(135deg, #012E72, #002DB5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
          </svg>
        </div>
      </div>
      <div style={{ textAlign: "center" }}>
        <p style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: "6px" }}>
          Waiting for others
        </p>
        <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "rgba(255,255,255,0.2)" }}>
          Share the link so others can join
        </p>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────
export default function VideoCallPage() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const channel    = params.get("channel") || "paladin-consultation";

  const [status,       setStatus]       = useState("idle"); // idle | connecting | connected | error | ended
  const [errorMsg,     setErrorMsg]     = useState("");
  const [audioMuted,   setAudioMuted]   = useState(false);
  const [videoMuted,   setVideoMuted]   = useState(false);
  const [remoteUsers,  setRemoteUsers]  = useState([]);
  const [localAudio,   setLocalAudio]   = useState(null);
  const [localVideo,   setLocalVideo]   = useState(null);
  const [copied,       setCopied]       = useState(false);

  const clientRef = useRef(null);

  const join = useCallback(async () => {
    setStatus("connecting");
    setErrorMsg("");
    try {
      // Fetch Agora token
      const uid = Math.floor(Math.random() * 100000);
      const tokenRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/agora/token?channel=${encodeURIComponent(channel)}&uid=${uid}`
      );
      if (!tokenRes.ok) {
        const err = await tokenRes.json();
        throw new Error(err.error || "Failed to get call token.");
      }
      const { appId, token } = await tokenRes.json();

      // Create Agora client
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;

      // Handle remote users
      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        setRemoteUsers((prev) => {
          const exists = prev.find((u) => u.uid === user.uid);
          return exists
            ? prev.map((u) => u.uid === user.uid ? user : u)
            : [...prev, user];
        });
        if (mediaType === "audio") {
          user.audioTrack?.play();
        }
      });

      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "audio") user.audioTrack?.stop();
        setRemoteUsers((prev) => prev.map((u) => u.uid === user.uid ? user : u));
      });

      client.on("user-left", (user) => {
        setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
      });

      // Create local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
      setLocalAudio(audioTrack);
      setLocalVideo(videoTrack);

      // Join and publish
      await client.join(appId, channel, token, uid);
      await client.publish([audioTrack, videoTrack]);

      setStatus("connected");
    } catch (err) {
      console.error("Video call error:", err);
      setErrorMsg(err.message || "Could not start the call.");
      setStatus("error");
    }
  }, [channel]);

  useEffect(() => {
    join();
    return () => {
      // Cleanup on unmount
      const client = clientRef.current;
      if (client) {
        client.leave().catch(() => {});
      }
    };
  }, [join]);

  const toggleAudio = async () => {
    if (!localAudio) return;
    await localAudio.setEnabled(audioMuted);
    setAudioMuted((v) => !v);
  };

  const toggleVideo = async () => {
    if (!localVideo) return;
    await localVideo.setEnabled(videoMuted);
    setVideoMuted((v) => !v);
  };

  const leaveCall = async () => {
    localAudio?.close();
    localVideo?.close();
    try { await clientRef.current?.leave(); } catch (_) {}
    setStatus("ended");
    setRemoteUsers([]);
    setLocalAudio(null);
    setLocalVideo(null);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Screens ───────────────────────────────────────────────────────

  if (status === "error") {
    return (
      <div style={{ minHeight: "100vh", background: "#0f0f1e", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ background: "#1a1a2e", borderRadius: "24px", padding: "48px", maxWidth: "480px", width: "100%", textAlign: "center", border: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "rgba(220,38,38,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: "#fff", letterSpacing: "0.06em", marginBottom: "12px" }}>
            Unable to Start Call
          </h2>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "rgba(255,255,255,0.45)", lineHeight: 1.7, marginBottom: "32px" }}>
            {errorMsg || "An error occurred while connecting. Please check your camera and microphone permissions, then try again."}
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <button onClick={() => navigate(-1)}
              style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "12px 24px", cursor: "pointer" }}>
              Go Back
            </button>
            <button onClick={() => { setStatus("idle"); join(); }}
              style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", border: "none", borderRadius: "12px", padding: "12px 24px", cursor: "pointer" }}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "ended") {
    return (
      <div style={{ minHeight: "100vh", background: "#0f0f1e", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ background: "#1a1a2e", borderRadius: "24px", padding: "48px", maxWidth: "420px", width: "100%", textAlign: "center", border: "1px solid rgba(255,255,255,0.07)" }}>
          <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "rgba(1,46,114,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(0,45,181,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: "#fff", letterSpacing: "0.06em", marginBottom: "12px" }}>
            Call Ended
          </h2>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "rgba(255,255,255,0.4)", marginBottom: "32px" }}>
            You have left the consultation call.
          </p>
          <button onClick={() => window.close()}
            style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", border: "none", borderRadius: "12px", padding: "14px 32px", cursor: "pointer" }}>
            Close Window
          </button>
        </div>
      </div>
    );
  }

  // ── Main call UI ──────────────────────────────────────────────────
  const firstRemote = remoteUsers[0] || null;

  return (
    <div style={{ minHeight: "100vh", background: "#0f0f1e", display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
      <style>{`
        @keyframes ping {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.15); opacity: 0; }
        }
      `}</style>

      {/* ── Top bar ── */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 20, padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, transparent 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "linear-gradient(135deg, #012E72, #002DB5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
            </svg>
          </div>
          <div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)" }}>Paladin Consultation</p>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", fontWeight: 700, color: "#fff", letterSpacing: "0.06em" }}>
              {channel}
            </p>
          </div>
        </div>

        {/* Copy link */}
        <button onClick={copyLink}
          style={{ display: "flex", alignItems: "center", gap: "8px", fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: copied ? "#4ade80" : "rgba(255,255,255,0.55)", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px", padding: "8px 16px", cursor: "pointer", transition: "all 0.2s" }}>
          {copied ? (
            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Copied!</>
          ) : (
            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> Share Link</>
          )}
        </button>
      </div>

      {/* ── Main video area ── */}
      <div style={{ flex: 1, position: "relative" }}>
        {/* Remote (or waiting) */}
        {status === "connecting" ? (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#0f0f1e" }}>
            <div style={{ textAlign: "center" }}>
              <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(0,45,181,0.5)" strokeWidth="2" style={{ margin: "0 auto 16px" }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>Connecting…</p>
            </div>
          </div>
        ) : firstRemote ? (
          <RemoteVideo user={firstRemote} />
        ) : (
          <WaitingScreen />
        )}

        {/* Multiple remote users grid (2+) */}
        {remoteUsers.length > 1 && (
          <div style={{ position: "absolute", top: "72px", right: "20px", display: "flex", flexDirection: "column", gap: "8px", zIndex: 9 }}>
            {remoteUsers.slice(1).map((u) => (
              <div key={u.uid} style={{ width: "140px", height: "105px", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", background: "#1a1a2e" }}>
                <RemoteVideo user={u} />
              </div>
            ))}
          </div>
        )}

        {/* Local video (picture-in-picture) */}
        {status === "connected" && (
          <LocalVideo track={localVideo} videoMuted={videoMuted} />
        )}
      </div>

      {/* ── Control bar ── */}
      <div style={{ position: "relative", zIndex: 20, padding: "20px 24px 28px", background: "linear-gradient(0deg, rgba(0,0,0,0.75) 0%, transparent 100%)", display: "flex", alignItems: "center", justifyContent: "center", gap: "16px" }}>

        {/* Status indicator */}
        {status === "connected" && (
          <div style={{ position: "absolute", left: "24px", display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 8px rgba(74,222,128,0.6)" }} />
            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)" }}>
              Live · {remoteUsers.length} other{remoteUsers.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* Mute mic */}
        <button
          onClick={toggleAudio}
          disabled={status !== "connected"}
          title={audioMuted ? "Unmute microphone" : "Mute microphone"}
          style={{ width: "56px", height: "56px", borderRadius: "50%", border: "none", cursor: status === "connected" ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", background: audioMuted ? "#dc2626" : "rgba(255,255,255,0.12)", color: "#fff", opacity: status !== "connected" ? 0.4 : 1 }}
          onMouseEnter={e => { if (status === "connected") e.currentTarget.style.background = audioMuted ? "#b91c1c" : "rgba(255,255,255,0.2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = audioMuted ? "#dc2626" : "rgba(255,255,255,0.12)"; }}
        >
          <MicIcon muted={audioMuted} />
        </button>

        {/* End call */}
        <button
          onClick={leaveCall}
          disabled={status !== "connected"}
          title="Leave call"
          style={{ width: "68px", height: "68px", borderRadius: "50%", border: "none", cursor: status === "connected" ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", background: "#dc2626", color: "#fff", transition: "all 0.2s", boxShadow: "0 4px 20px rgba(220,38,38,0.5)", opacity: status !== "connected" ? 0.4 : 1 }}
          onMouseEnter={e => { if (status === "connected") e.currentTarget.style.background = "#b91c1c"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#dc2626"; }}
        >
          <PhoneOffIcon />
        </button>

        {/* Mute camera */}
        <button
          onClick={toggleVideo}
          disabled={status !== "connected"}
          title={videoMuted ? "Turn on camera" : "Turn off camera"}
          style={{ width: "56px", height: "56px", borderRadius: "50%", border: "none", cursor: status === "connected" ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", background: videoMuted ? "#dc2626" : "rgba(255,255,255,0.12)", color: "#fff", opacity: status !== "connected" ? 0.4 : 1 }}
          onMouseEnter={e => { if (status === "connected") e.currentTarget.style.background = videoMuted ? "#b91c1c" : "rgba(255,255,255,0.2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = videoMuted ? "#dc2626" : "rgba(255,255,255,0.12)"; }}
        >
          <CamIcon muted={videoMuted} />
        </button>
      </div>
    </div>
  );
}
